import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Payment,
    Receipt,
    People,
    Link as LinkIcon,
    TrendingUp,
    TrendingDown,
    QrCode,
    Share,
    ContentCopy,
    CheckCircle,
    Refresh,
    ArrowForward,
    FlashOn,
    Speed,
    AccountBalanceWallet,
    CalendarToday,
    ShowChart,
    DateRange,
    SwapHoriz
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { XhrContext } from '../../contexts/XhrContext';
import { hosts } from '../../config/environment';
import { fetchPaymentsInRange } from '../../utils/cashReport';
import { fetchCaissierTerminal } from '../../services/posService';
import CirclePieces from '../../components/pos/CirclePieces';
import CashierSidebar from '../../components/cashier/CashierSidebar';
import CashierHeader from '../../components/cashier/CashierHeader';

const formatAmount = (num, currency) => {
    const n = Number(num) ?? 0;
    if (currency === 'USD') {
        return '$' + n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    if (n >= 1e6) return (n / 1e6).toFixed(2).replace(/\.0$/, '') + 'M FC';
    if (n >= 1e3) return (n / 1e3).toFixed(1).replace(/\.0$/, '') + 'K FC';
    return n.toLocaleString('fr-FR', { maximumFractionDigits: 0 }) + ' FC';
};

const calcTrend = (current, previous) => {
    const cur = current ?? 0;
    const prev = previous ?? 0;
    if (prev === 0 && cur === 0) return 0;
    if (prev === 0 && cur > 0) return 100;
    return ((cur - prev) / prev) * 100;
};

const EMPTY_DASHBOARD = {
    dailyAmount: 0,
    lastDayAmount: 0,
    weeklyAmount: 0,
    lastWeekAmount: 0,
    monthlyAmount: 0,
    lastMonthAmount: 0,
};

const CashierHomeScreen = () => {
    const { user, selectedOrganization } = useAuth();
    const { getRequest } = useContext(XhrContext);
    const navigate = useNavigate();

    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [displayCurrency, setDisplayCurrency] = useState('CDF');
    const [loading, setLoading] = useState(false);
    const [loadingDashboard, setLoadingDashboard] = useState(false);
    const [terminal, setTerminal] = useState(null);
    const [terminalError, setTerminalError] = useState(null);
    const [qrRender, setQrRender] = useState(null);
    const [copied, setCopied] = useState(false);

    const [dashboard, setDashboard] = useState(EMPTY_DASHBOARD);
    const [pendingRequests, setPendingRequests] = useState(0);
    const [activeLinks, setActiveLinks] = useState(0);

    const normalizeRole = (roleName) => {
        if (!roleName) return 'admin';
        const n = String(roleName).toLowerCase().trim();
        if (n.includes('caissier') || n === 'cashier') return 'cashier';
        if (n.includes('admin') || n === 'administrator') return 'admin';
        return n;
    };

    useEffect(() => {
        if (!selectedOrganization) return;
        const role = normalizeRole(selectedOrganization.selectedRole || selectedOrganization.role);
        if (role === 'admin' || (role === 'both' && !selectedOrganization.selectedRole)) {
            navigate('/dashboard', { replace: true });
            return;
        }
        if (selectedOrganization.id) {
            loadTerminal();
            loadDashboard();
            loadPendingCounts();
        }
    }, [selectedOrganization, navigate]);

    useEffect(() => {
        if (selectedOrganization?.id) loadDashboard();
    }, [displayCurrency]);

    useEffect(() => {
        setQrRender(terminal?.tag ? { id: terminal.tag } : null);
    }, [terminal?.tag]);

    const loadTerminal = useCallback(async () => {
        if (!selectedOrganization?.id || !user?.id) return;
        setLoading(true);
        try {
            const t = await fetchCaissierTerminal(selectedOrganization.id, user.id);
            setTerminal(t || null);
            setTerminalError(null);
        } catch (e) {
            console.warn('[CashierHomeScreen] Terminal load failed:', e);
            setTerminalError('Terminal indisponible. Réessayez plus tard.');
        } finally {
            setLoading(false);
        }
    }, [selectedOrganization?.id, user?.id]);

    // Encaissements réussis du caissier (pos-service), même source que son rapport de caisse.
    // Chaque période est comparée au même laps de temps de la période précédente.
    const loadDashboard = useCallback(async () => {
        if (!selectedOrganization?.id || !user?.id) return;
        setLoadingDashboard(true);
        try {
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const yesterday = new Date(today);
            yesterday.setDate(today.getDate() - 1);
            const week = new Date(today);
            week.setDate(today.getDate() - ((today.getDay() + 6) % 7)); // lundi
            const lastWeek = new Date(week);
            lastWeek.setDate(week.getDate() - 7);
            const month = new Date(now.getFullYear(), now.getMonth(), 1);
            const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

            const from = lastWeek < lastMonth ? lastWeek : lastMonth;
            const payments = (await fetchPaymentsInRange(selectedOrganization.id, from, now))
                .filter((p) => p.success && p.cashierId === user.id && p.currency === displayCurrency);
            const sum = (start, end) => payments
                .filter((p) => {
                    const at = p.paidAt || p.createdAt;
                    return at && at >= start && at <= end;
                })
                .reduce((total, p) => total + p.amount, 0);
            const sameSpan = (prevStart, curStart) => new Date(prevStart.getTime() + (now - curStart));

            setDashboard({
                dailyAmount: sum(today, now),
                lastDayAmount: sum(yesterday, sameSpan(yesterday, today)),
                weeklyAmount: sum(week, now),
                lastWeekAmount: sum(lastWeek, sameSpan(lastWeek, week)),
                monthlyAmount: sum(month, now),
                lastMonthAmount: sum(lastMonth, sameSpan(lastMonth, month)),
            });
        } catch (e) {
            console.error('Error loading dashboard:', e);
            setDashboard(EMPTY_DASHBOARD);
        } finally {
            setLoadingDashboard(false);
        }
    }, [selectedOrganization?.id, user?.id, displayCurrency]);

    const loadPendingCounts = useCallback(async () => {
        if (!selectedOrganization?.id) return;
        try {
            const [reqRes, linkRes] = await Promise.all([
                getRequest('/pos/payment-requests', { params: { org: selectedOrganization.id, sts: 'pending', lmt: 100 } }).catch(() => ({ data: {} })),
                getRequest('/pos/payment-links', { params: { org: selectedOrganization.id, sts: 'active', lmt: 100 } }).catch(() => ({ data: {} }))
            ]);
            setPendingRequests(reqRes?.data?.contents?.length ?? 0);
            setActiveLinks(linkRes?.data?.contents?.length ?? 0);
        } catch (e) {
            console.warn('Error loading pending counts:', e);
        }
    }, [selectedOrganization?.id, getRequest]);

    const handleRefresh = () => {
        loadTerminal();
        loadDashboard();
        loadPendingCounts();
    };

    const copyUrl = async () => {
        const url = `${hosts.checkout}/payment-terminals/${terminal?.tag}`;
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (e) {
            console.error('Copy failed:', e);
        }
    };

    const shareUrl = async () => {
        const url = `${hosts.checkout}/payment-terminals/${terminal?.tag}`;
        if (navigator.share) {
            try {
                await navigator.share({ title: 'Terminal de paiement', text: `Payer: ${url}`, url });
            } catch (e) {
                if (e.name !== 'AbortError') copyUrl();
            }
        } else {
            copyUrl();
        }
    };

    const getLogoUrl = () => {
        if (!selectedOrganization?.id) return '';
        return `${hosts.api}/exposed/v1/mda/images/show?target=logo&target_id=${selectedOrganization.id}`;
    };

    const trendDay = calcTrend(dashboard.dailyAmount, dashboard.lastDayAmount);
    const trendWeek = calcTrend(dashboard.weeklyAmount, dashboard.lastWeekAmount);
    const trendMonth = calcTrend(dashboard.monthlyAmount, dashboard.lastMonthAmount);

    const quickActions = [
        { label: 'Nouvelle Demande', icon: Payment, path: '/cashier/payment-requests', description: 'Créer une demande de paiement' },
        { label: 'Lien de Paiement', icon: LinkIcon, path: '/cashier/payment-links', description: 'Générer un lien' },
        // { label: 'Nouvelle Facture', icon: Receipt, path: '/cashier/invoices', description: 'Créer une facture' }, // Factures / Articles masqués pour la mise en dev : décommenter pour réactiver
        { label: 'Clients', icon: People, path: '/cashier/customers', description: 'Voir et gérer les clients' }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-amber-50/20 to-slate-50 flex">
            <CashierSidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />

            <div className="flex-1 min-h-screen transition-all duration-300 overflow-hidden flex flex-col lg:ml-[var(--cashier-sidebar-w,18rem)] transition-[margin] duration-300">
                <CashierHeader
                    onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
                    onRefresh={handleRefresh}
                    loading={loading || loadingDashboard}
                    terminal={terminal}
                />

                <main className="flex-1 p-5 lg:p-8 overflow-y-auto">
                    {/* Bannière + devise */}
                    <div className="mb-8">
                        <div className="relative bg-white/90 backdrop-blur-sm rounded-3xl border border-amber-200/50 shadow-xl shadow-amber-500/5 overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-r from-amber-400/10 via-orange-400/5 to-transparent pointer-events-none" />
                            <div className="relative px-6 lg:px-8 py-6">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                                    <div className="flex items-center gap-5">
                                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/30 ring-4 ring-amber-500/10">
                                            <AccountBalanceWallet className="text-white text-3xl" />
                                        </div>
                                        <div>
                                            <h1 className="text-2xl lg:text-3xl font-bold text-slate-800 tracking-tight">Tableau de bord</h1>
                                            <p className="text-slate-600 mt-1">Vue d&apos;ensemble de votre activité en temps réel</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 flex-shrink-0">
                                        <span className="text-sm font-semibold text-slate-600">Devise des montants</span>
                                        <div className="flex p-1 bg-slate-100 rounded-xl border border-slate-200/80">
                                            <button
                                                type="button"
                                                onClick={() => setDisplayCurrency('CDF')}
                                                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold transition-all ${
                                                    displayCurrency === 'CDF'
                                                        ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md'
                                                        : 'text-slate-600 hover:bg-slate-200/80'
                                                }`}
                                            >
                                                <SwapHoriz className="text-lg" />
                                                CDF
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setDisplayCurrency('USD')}
                                                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold transition-all ${
                                                    displayCurrency === 'USD'
                                                        ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md'
                                                        : 'text-slate-600 hover:bg-slate-200/80'
                                                }`}
                                            >
                                                USD
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Encaissements : aujourd'hui / cette semaine / ce mois */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
                        {[
                            { label: "Aujourd'hui", value: dashboard.dailyAmount, trend: trendDay, icon: CalendarToday },
                            { label: 'Cette semaine', value: dashboard.weeklyAmount, trend: trendWeek, icon: DateRange },
                            { label: 'Ce mois', value: dashboard.monthlyAmount, trend: trendMonth, icon: ShowChart }
                        ].map(({ label, value, trend, icon: Icon }) => (
                            <div
                                key={label}
                                className="bg-white/95 backdrop-blur-sm rounded-2xl border border-slate-200/80 shadow-xl shadow-slate-200/30 p-6 hover:shadow-amber-500/10 hover:border-amber-200/50 transition-all"
                            >
                                <div className="flex items-center justify-between mb-3">
                                    <span className="text-sm font-semibold text-slate-600">{label}</span>
                                    {loadingDashboard ? (
                                        <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        <span className={`inline-flex items-center text-xs font-bold px-2.5 py-1 rounded-lg ${
                                            trend > 0 ? 'bg-emerald-100 text-emerald-700' : trend < 0 ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600'
                                        }`}>
                                            {trend > 0 && <TrendingUp className="w-3.5 h-3.5 mr-0.5" />}
                                            {trend < 0 && <TrendingDown className="w-3.5 h-3.5 mr-0.5" />}
                                            {trend.toFixed(0)}%
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                                        <Icon className="text-amber-600" />
                                    </div>
                                    <p className="text-xl font-bold text-slate-900">
                                        {loadingDashboard ? '—' : formatAmount(value, displayCurrency)}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Carte En attente + Liens actifs */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-8">
                        <div className="bg-white/95 backdrop-blur-sm rounded-2xl border border-amber-200/50 shadow-xl shadow-slate-200/30 p-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                                        <FlashOn className="text-amber-600 text-xl" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold text-slate-600">Demandes en attente</p>
                                        <p className="text-2xl font-bold text-slate-900">{pendingRequests}</p>
                                    </div>
                                </div>
                                <span className={`px-3 py-1.5 rounded-xl text-sm font-medium ${pendingRequests > 0 ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600'}`}>
                                    {pendingRequests > 0 ? 'Action requise' : 'Aucune'}
                                </span>
                            </div>
                            <button
                                type="button"
                                onClick={() => navigate('/cashier/payment-requests')}
                                className="mt-4 w-full py-2.5 rounded-xl text-sm font-semibold text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200/60 transition-colors"
                            >
                                Voir les demandes
                            </button>
                        </div>
                        <div className="bg-white/95 backdrop-blur-sm rounded-2xl border border-slate-200/80 shadow-xl shadow-slate-200/30 p-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                                        <LinkIcon className="text-blue-600 text-xl" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold text-slate-600">Liens actifs</p>
                                        <p className="text-2xl font-bold text-slate-900">{activeLinks}</p>
                                    </div>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => navigate('/cashier/payment-links')}
                                className="mt-4 w-full py-2.5 rounded-xl text-sm font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200/60 transition-colors"
                            >
                                Voir les liens
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
                        {/* Terminal + QR */}
                        <div className="lg:col-span-2">
                            <div className="relative bg-white/95 backdrop-blur-sm rounded-3xl border border-slate-200/80 shadow-xl shadow-slate-200/50 overflow-hidden">
                                <div className="absolute inset-0 bg-gradient-to-r from-amber-400/5 via-transparent to-transparent pointer-events-none" />
                                <div className="relative p-6 lg:p-8">
                                    <div className="flex items-center justify-between mb-6">
                                        <div>
                                            <h2 className="text-xl font-bold text-slate-800">Terminal de paiement</h2>
                                            <p className="text-slate-600 text-sm mt-0.5">Scannez pour recevoir des paiements</p>
                                        </div>
                                        {terminal?.tag && (
                                            <div className="px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl shadow-lg shadow-amber-500/30 border border-amber-400/30">
                                                <span className="text-sm font-bold text-white tracking-wide">POS {terminal.tag}</span>
                                            </div>
                                        )}
                                    </div>

                                    {qrRender?.id ? (
                                        <div className="flex flex-col items-center space-y-6">
                                            {terminalError && (
                                                <div className="w-full px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-2">
                                                    <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                                                    <p className="text-sm text-amber-700">{terminalError}</p>
                                                </div>
                                            )}
                                            <div className="relative p-8 bg-white rounded-3xl border-2 border-slate-200/80 shadow-inner">
                                                <CirclePieces posId={qrRender.id} size={12} />
                                            </div>
                                            <div className="w-full flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-2xl border border-slate-200">
                                                <input
                                                    type="text"
                                                    readOnly
                                                    value={`${hosts.checkout}/payment-terminals/${terminal?.tag}`}
                                                    className="flex-1 bg-transparent text-sm font-mono text-slate-700 outline-none min-w-0"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={copied ? undefined : copyUrl}
                                                    className={`p-2.5 rounded-xl transition-all ${copied ? 'bg-emerald-500 text-white' : 'bg-white border border-slate-200 hover:border-amber-300 hover:bg-amber-50 text-slate-600'}`}
                                                    title={copied ? 'Copié' : 'Copier'}
                                                >
                                                    {copied ? <CheckCircle className="text-xl" /> : <ContentCopy className="text-xl" />}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={shareUrl}
                                                    className="p-2.5 rounded-xl bg-white border border-slate-200 hover:border-amber-300 hover:bg-amber-50 text-slate-600 transition-all"
                                                    title="Partager"
                                                >
                                                    <Share className="text-xl" />
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center py-16">
                                            <div className="w-28 h-28 bg-slate-100 rounded-3xl mx-auto mb-4 flex items-center justify-center">
                                                <QrCode className="text-slate-400 text-5xl" />
                                            </div>
                                            <p className="text-slate-600 font-medium mb-1">{terminalError || 'Aucun terminal'}</p>
                                            <p className="text-sm text-slate-500 mb-4">Actualisez pour charger votre terminal</p>
                                            <button
                                                type="button"
                                                onClick={handleRefresh}
                                                disabled={loading}
                                                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-semibold hover:from-amber-600 hover:to-orange-600 disabled:opacity-50"
                                            >
                                                <Refresh className={loading ? 'animate-spin' : ''} />
                                                {loading ? 'Chargement...' : 'Actualiser'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Actions rapides + Orga */}
                        <div className="space-y-6">
                            <div className="bg-white/95 backdrop-blur-sm rounded-3xl border border-slate-200/80 shadow-xl shadow-slate-200/50 p-6">
                                <div className="flex items-center justify-between mb-5">
                                    <h2 className="text-lg font-bold text-slate-800">Actions rapides</h2>
                                    <Speed className="text-amber-500" />
                                </div>
                                <div className="space-y-3">
                                    {quickActions.map((action, idx) => {
                                        const Icon = action.icon;
                                        return (
                                            <button
                                                key={idx}
                                                type="button"
                                                onClick={() => navigate(action.path, { state: terminal?.id ? { terminal_id: terminal.id } : {} })}
                                                className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 border-slate-200 hover:border-amber-200 hover:bg-amber-50/50 transition-all text-left group"
                                            >
                                                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-md group-hover:scale-105 transition-transform">
                                                    <Icon className="text-white text-xl" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-bold text-slate-900">{action.label}</p>
                                                    <p className="text-xs text-slate-500 truncate">{action.description}</p>
                                                </div>
                                                <ArrowForward className="text-slate-400 group-hover:text-amber-600 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {selectedOrganization && (
                                <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl border border-slate-700/50 shadow-xl p-6">
                                    <div className="flex items-center gap-4 mb-4">
                                        <div className="w-14 h-14 rounded-2xl bg-slate-700/80 flex items-center justify-center overflow-hidden flex-shrink-0">
                                            <img
                                                src={getLogoUrl()}
                                                alt=""
                                                className="w-full h-full object-cover"
                                                onError={(e) => { e.target.style.display = 'none'; }}
                                            />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="font-bold text-white truncate">{selectedOrganization.name}</p>
                                            <div className="flex items-center gap-2 mt-0.5">
                                                <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                                                <span className="text-xs text-slate-400">Caissier</span>
                                            </div>
                                        </div>
                                    </div>
                                    {terminal?.tag && (
                                        <div className="pt-4 border-t border-slate-700/50">
                                            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Terminal</p>
                                            <p className="font-mono font-bold text-amber-400">{terminal.tag}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default CashierHomeScreen;
